/*
 * @Author: zhoulang zhoulang@kuaishou.com
 * @Date: 2024-06-17 14:34:23
 * @LastEditors: zhoulang zhoulang@kuaishou.com
 * @LastEditTime: 2024-10-12 10:31:14
 * @FilePath: /kibt-fe-shop-c-krn/src/component/GoodsBenefit/index.tsx
 * @Description: 泛货架领域下商品卡腰封&利益点标签，统一由后端下发
 *  FLASH_SALE 秒杀
    MILLION_BONUS  达人优惠补贴
    HOT_ITEMS 爆款直降
    PRODUCT_COUPONS  商品券
    MERCHANT_SHIPPING_FEE_DISCOUNT  运费折扣活动
    SHIPPING_FEE_COUPONS 运费券
    SIMILAR_LOW_PRICE  同品低价
    PRODUCT_LIST_RED_PACKET 红包雨
 */
import React, { CSSProperties, memo, useMemo, useState } from 'react';
import { View, Text, TextStyle, ViewStyle, StyleProp } from 'react-native';
import { useCountDown } from 'ahooks';
import { withZeroText } from '@/common/utils/withZeroText';
import ImageAutoScale from '../ImageAutoScale';
import { isIOS } from '@/common/device-info';
import { scaleW } from '@/common/style';
import { KImageBackground } from '../RtlLayout';

export const useCountDownText = ({ countDownMills, onEnd }) => {
    const [isEnd, setIsEnd] = useState(false);
    const [_, formateRes] = useCountDown({
        leftTime: countDownMills, // 设置倒计时总时间
        interval: 1000,
        onEnd: () => {
            onEnd?.();
            setIsEnd(true);
        },
    });

    const countDownText = [formateRes.days, withZeroText(formateRes.hours), withZeroText(formateRes.minutes), withZeroText(formateRes.seconds)]
        .filter(Boolean)
        .join(':');

    return {
        isEnd,
        countDownText,
    };
};

export const CountDownText = ({ countDownMills, onEnd, children }) => {
    const [isEnd, setIsEnd] = useState(false);

    const [_, formateRes] = useCountDown({
        leftTime: countDownMills, // 设置倒计时总时间
        interval: 1000,
        onEnd: () => {
            onEnd?.();
            setIsEnd(true);
        },
    });

    const countDownText = [formateRes.days, withZeroText(formateRes.hours), withZeroText(formateRes.minutes), withZeroText(formateRes.seconds)]
        .filter(Boolean)
        .join(':');

    // 性能优化，如果不变，不要一直countdown render
    const jsx = useMemo(() => {
        return children({ isEnd, countDownText });
    }, [isEnd, countDownText, children]);

    return jsx;
};

// 利益点标签，只有短视频主卡有
export const GoodsBenefitTag = ({ tagInfo, onEnd }: { tagInfo: any; onEnd?: () => void }) => {
    const { name, countDownMills, type } = tagInfo ?? {};
    const bgColor = ['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type)
        ? 'background: rgba(76, 167, 162, 0.1)'
        : 'rgba(255, 73, 6, 0.1)';
    const textColor = ['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type) ? 'rgba(76, 167, 162, 1)' : 'rgba(255, 73, 6, 1)';
    return (
        <CountDownText countDownMills={countDownMills} onEnd={onEnd}>
            {({ isEnd, countDownText }) => {
                const renderText = countDownMills > 0 ? `${name}·${countDownText}` : name;
                return !isEnd ? (
                    <View style={{"flexDirection":"row","alignItems":"center"}}>
                        <Text
                            style={{
                                backgroundColor: bgColor,
                                color: textColor,
                                fontSize: 11,
                                lineHeight: 13,
                                paddingHorizontal: 4,
                                paddingVertical: 2.5,
                                borderRadius: 3,
                            }}>
                            {renderText}
                        </Text>
                    </View>
                ) : null;
            }}
        </CountDownText>
    );
};

export enum IBannerType {
    Normal,
    ShoppingCar,
    MyShoppingCar,
    RecoMain,
    RecoList,
    Detail,
    DetailSameProducts,
}

// 不考虑运费券的情况
export const isActivityBannerWithoutShippingCoupon = benefitInfo => {
    const { girdleModule } = benefitInfo ?? {};
    const { type } = girdleModule ?? {};
    // 活动类的腰封需要隐藏商品卡右侧或底部的辅助区域标签
    if (['FLASH_SALE', 'MILLION_BONUS', 'HOT_ITEMS', 'PRODUCT_COUPONS', 'PRODUCT_LIST_RED_PACKET'].includes(type)) {
        return true;
    }
    return false;
};

export const isActivityBanner = benefitInfo => {
    const { girdleModule } = benefitInfo ?? {};
    const { type } = girdleModule ?? {};
    // 活动类的腰封需要隐藏商品卡右侧或底部的辅助区域标签
    if (
        [
            'FLASH_SALE',
            'MILLION_BONUS',
            'HOT_ITEMS',
            'PRODUCT_COUPONS',
            'PRODUCT_LIST_RED_PACKET',
            'MERCHANT_SHIPPING_FEE_DISCOUNT',
            'SHIPPING_FEE_COUPONS',
        ].includes(type)
    ) {
        return true;
    }
    return false;
};

export const isLowPriceBanner = benefitInfo => {
    const { girdleModule } = benefitInfo ?? {};
    const { type } = girdleModule ?? {};
    // 同品低价
    if (['SIMILAR_LOW_PRICE'].includes(type)) {
        return true;
    }
    return false;
};

export const getTagCode = benefitInfo => {
    const { girdleModule } = benefitInfo ?? {};
    const { type } = girdleModule ?? {};
    return type;
};

type PartConfig = {
    color: string; // 文案色值
    bgColor: string; // 背景色值(倒计时色值)
    icon: string; // 图标
    value: string; // 文案
    type: 'COUNT_DOWN_TIME' | 'TEXT'; // 类型
};

// 商品图底部腰封
export const GoodsBenefitBanner = ({
    benefitInfo,
    onEnd,
    bannerType = IBannerType.RecoList,
}: {
    benefitInfo: any;
    onEnd?: () => void;
    bannerType?: IBannerType;
}) => {
    const { style, girdleModule } = benefitInfo ?? {};
    // @tangziyao 2024-12-9 同后端更新腰封协议，腰封的展示内容通过左右来下发，在style中新增加left和right字段，前端不关心内容含义，只处理展示逻辑
    // 如：
    //     left: {
    //         color: 'rgba(0, 0, 0, 0.88)',
    //         bgColor: null,
    //         icon: 'https://media-cloud-api.staging.kuaishou.com/kimg/…host/ChYwOGQ5ZmNhYTAxMTBmMTkwZGFkYTA0ENfM1y8.webp',
    //         value: 'Oferta Relâmpago',
    //         type: 'TEXT'
    //    }
    // 表示左边类型为字符串，带icon，内容为Oferta Relâmpago，type还有可能为倒计时countdown；右边同理
    // 注意：为了防止后续协议改写，影响到其他后端方向的透传，这里的left和right都是stringify后的字符串，需要前端parse后使用

    const { bgImg = '', activityIcon = '', activityColor = 'rgba(0, 0, 0, 0.88)', LEFT = '', RIGHT = '' } = style ?? {};
    const { name = '', countDownMills: _countDownMills = 0, type } = girdleModule ?? {};
    const leftConfig = (LEFT ? JSON.parse(LEFT) : {}) as PartConfig;
    const rightConfig = (RIGHT ? JSON.parse(RIGHT) : {}) as PartConfig;
    const countDownMills =
        leftConfig.type === 'COUNT_DOWN_TIME' ? +leftConfig.value : rightConfig.type === 'COUNT_DOWN_TIME' ? +rightConfig.value : _countDownMills;

    const activityText = (() => {
        // 短视频主卡的秒杀腰封且有倒计时的时候，不展示文本文案
        if ([IBannerType.RecoMain].includes(bannerType) && type === 'FLASH_SALE' && countDownMills > 0) {
            return '';
        }
        return leftConfig.value || name;
    })();

    const showRight = (() => {
        // 短视频主卡的非秒杀腰封不展示倒计时,商详页相似商品不展示倒计时
        if (([IBannerType.RecoMain].includes(bannerType) && type !== 'FLASH_SALE') || IBannerType.DetailSameProducts === bannerType) {
            return false;
        }
        return true;
    })();

    const showCountDown = (() => {
        if (countDownMills <= 0) {
            return false;
        }
        // 短视频主卡的非秒杀腰封不展示倒计时,商详页相似商品不展示倒计时
        if (([IBannerType.RecoMain].includes(bannerType) && type !== 'FLASH_SALE') || IBannerType.DetailSameProducts === bannerType) {
            return false;
        }

        return true;
    })();

    const bgWrapStyle: (CSSProperties | CSSProperties[]) & StyleProp<ViewStyle> = (() => {
        const bgHeight = {
            [IBannerType.ShoppingCar]: 20,
            [IBannerType.RecoMain]: 24,
            [IBannerType.Detail]: 36,
            [IBannerType.MyShoppingCar]: 18,
            [IBannerType.DetailSameProducts]: 18,
        };

        const getBgHeight = () => {
            if ([IBannerType.RecoList].includes(bannerType) && ['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type)) {
                return 20;
            }
            return bgHeight[bannerType] || 24;
        };

        const getWidth = () => {
            // 推荐双列场景下的运费折扣只展示一半宽度
            if ([IBannerType.RecoList].includes(bannerType) && ['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type)) {
                return 145;
            }
            if (bannerType === IBannerType.Detail) {
                return 260;
            }
            return '100%';
        };

        const getBgBorderRadius = () => {
            if ([IBannerType.ShoppingCar, IBannerType.MyShoppingCar].includes(bannerType)) {
                return 6;
            }
            if ([IBannerType.RecoMain].includes(bannerType)) {
                return 8;
            }
            return 0;
        };

        return {
            width: getWidth() || '100%',
            height: getBgHeight() || 24,
            borderBottomLeftRadius: getBgBorderRadius() || 0,
            borderBottomRightRadius: getBgBorderRadius() || 0,
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
        };
    })();

    const viewWrapStyle: ViewStyle = (() => {
        const paddingHorizontal = ['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type) ? 4 : 8;
        if ([IBannerType.RecoList].includes(bannerType) && (countDownMills > 0 || rightConfig.value)) {
            return {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingHorizontal,
                width: '100%',
            };
        }
        return {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
        };
    })();

    const iconStyle = (() => {
        if (bannerType === IBannerType.Detail) {
            return {
                marginRight: 4,
            };
        }
        if (
            ['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type) &&
            (bannerType === IBannerType.RecoList || bannerType === IBannerType.DetailSameProducts)
        ) {
            return {
                marginRight: 4,
            };
        }
        return {
            marginRight: 2,
        };
    })();

    const iconHeight = (() => {
        if (bannerType === IBannerType.ShoppingCar) {
            return 14;
        }
        if (bannerType === IBannerType.Detail) {
            return 20;
        }
        if (bannerType === IBannerType.MyShoppingCar) {
            return 10;
        }
        if (['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type) && bannerType === IBannerType.RecoList) {
            return 12;
        }
        if (bannerType === IBannerType.DetailSameProducts) {
            return scaleW(12);
        }
        return 16;
    })();

    const textStyle: TextStyle = (() => {
        if (
            (['SHIPPING_FEE_COUPONS', 'MERCHANT_SHIPPING_FEE_DISCOUNT'].includes(type) && bannerType === IBannerType.RecoList) ||
            bannerType === IBannerType.MyShoppingCar
        ) {
            return {
                fontSize: 9,
                lineHeight: 14,
                letterSpacing: isIOS ? -1 : -0.3,
                fontWeight: '700',
            };
        }
        if (bannerType === IBannerType.ShoppingCar) {
            return {
                fontSize: 11,
                lineHeight: 14,
                letterSpacing: isIOS ? -1 : type === 'FLASH_SALE' ? -0.7 : -0.3,
                fontWeight: '700',
            };
        }
        if (bannerType === IBannerType.Detail) {
            return {
                fontSize: scaleW(15),
                lineHeight: scaleW(21),
                letterSpacing: isIOS ? -1 : -0.3,
                fontWeight: '700',
            };
        }
        if (bannerType === IBannerType.DetailSameProducts) {
            return {
                fontSize: scaleW(9),
                lineHeight: 18,
                letterSpacing: isIOS ? -1 : -0.3,
                fontWeight: '700',
            };
        }
        return {
            fontSize: 12,
            lineHeight: 14,
            letterSpacing: isIOS ? -1 : -0.3,
            fontWeight: '700',
        };
    })();

    const countDownStyle: TextStyle = (() => {
        if (bannerType === IBannerType.RecoList && showCountDown) {
            return {
                fontSize: 11,
                fontWeight: '500',
            };
        }
    })();
    if (Object.keys(leftConfig).length || Object.keys(rightConfig).length) {
        return (
            <CountDownText countDownMills={countDownMills} onEnd={onEnd}>
                {({ isEnd, countDownText }) => {
                    return !isEnd ? (
                        <KImageBackground source={{ uri: bgImg }} resizeMode="cover" style={{ ...bgWrapStyle }}>
                            <View style={{ ...viewWrapStyle }}>
                                {/* left */}
                                <View style={{"flexDirection":"row","alignItems":"center"}}>
                                    {leftConfig.icon ? (
                                        <ImageAutoScale style={{ ...iconStyle }} source={{ uri: leftConfig.icon }} height={iconHeight} />
                                    ) : null}
                                    {activityText ? (
                                        <Text
                                            style={{
                                                ...textStyle,
                                                color: leftConfig.color || activityColor,
                                                fontSize: Math.min(scaleW(11), textStyle.fontSize),
                                            }}>
                                            {activityText}
                                        </Text>
                                    ) : null}
                                </View>
                                {/* right */}
                                {rightConfig.value && showRight ? (
                                    <Text
                                        style={{
                                            ...textStyle,
                                            ...countDownStyle,
                                            color: rightConfig.color || activityColor,
                                            fontSize: Math.min(scaleW(11), textStyle.fontSize),
                                        }}>
                                        {rightConfig.type === 'COUNT_DOWN_TIME' ? countDownText : rightConfig.value}
                                    </Text>
                                ) : null}
                            </View>
                        </KImageBackground>
                    ) : null;
                }}
            </CountDownText>
        );
    }

    return (
        <CountDownText countDownMills={countDownMills} onEnd={onEnd}>
            {({ isEnd, countDownText }) => {
                return !isEnd ? (
                    <KImageBackground source={{ uri: bgImg }} resizeMode="cover" style={{ ...bgWrapStyle }}>
                        <View style={{ ...viewWrapStyle }}>
                            <View style={{"flexDirection":"row","alignItems":"center"}}>
                                {activityIcon ? <ImageAutoScale style={{ ...iconStyle }} source={{ uri: activityIcon }} height={iconHeight} /> : null}
                                {activityText ? <Text style={{ ...textStyle, color: activityColor }}>{activityText}</Text> : null}
                            </View>
                            {showCountDown ? <Text style={{ ...textStyle, ...countDownStyle, color: activityColor }}>{countDownText}</Text> : null}
                        </View>
                    </KImageBackground>
                ) : null;
            }}
        </CountDownText>
    );
};
